import { ContractRowDTO } from '@shared/clientTypes/DTOs/ContractsDTO';
import {
  CLIENT_INVOICE_STATUS_LABELS,
  CONTRACT_CONTACT_FORM_LABELS,
  CONTRACT_STATUS_LABELS,
} from '@shared/constants';
import { ClientInvoiceStatus, ContractPaymentType, ContractStatus } from '@shared/enums';
import { stringify } from 'csv-stringify/sync';
import moment from 'moment';

export const DATE_FORMAT = 'DD.MM.YYYY';

export const formatDate = (value?: string | null) =>
  value && moment(value).isValid() ? moment(value).format(DATE_FORMAT) : '—';

export const formatMoney = (amount?: number | null, currency = 'RON') => {
  if (amount === null || amount === undefined || Number.isNaN(amount)) return '—';
  try {
    return new Intl.NumberFormat('ro-RO', {
      style: 'currency',
      currency,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return `${amount.toFixed(2)} ${currency}`;
  }
};

export const contractStatusTone = (status: ContractStatus) => {
  switch (status) {
    case ContractStatus.NoContract:
      return 'bg-surfaceHover text-customGrey';
    case ContractStatus.Overdue:
      return 'bg-customRed/10 text-customRed';
    case ContractStatus.FullyPaid:
    case ContractStatus.Completed:
      return 'bg-customGreen/10 text-customGreen';
    case ContractStatus.Cancelled:
    case ContractStatus.Refunded:
      return 'bg-surfaceHover text-customGrey';
    default:
      return 'bg-customGold/10 text-customGold';
  }
};

export const invoiceStatusTone = (status: ClientInvoiceStatus) => {
  switch (status) {
    case ClientInvoiceStatus.Overdue:
      return 'bg-customRed/10 text-customRed';
    case ClientInvoiceStatus.Paid:
      return 'bg-customGreen/10 text-customGreen';
    case ClientInvoiceStatus.Cancelled:
      return 'bg-surfaceHover text-customGrey';
    default:
      return 'bg-customGold/10 text-customGold';
  }
};

export const churnTone = (level?: 'low' | 'medium' | 'high') => {
  if (level === 'high') return 'text-customRed';
  if (level === 'medium') return 'text-customGold';
  if (level === 'low') return 'text-customGreen';
  return 'text-customGrey';
};

export const frequencyLabel = (
  contract: Pick<ContractRowDTO, 'paymentType' | 'intervalMonths'>
) => {
  if (contract.paymentType === ContractPaymentType.Full) return 'One-time';
  if (contract.intervalMonths === 1) return 'Monthly';
  return `Every ${contract.intervalMonths} months`;
};

const CSV_HEADERS = [
  'Client',
  'Contract number',
  'Contract',
  'AD (adiacent)',
  'Student',
  'Parent name',
  'Parent email',
  'Parent phone',
  'Strategist',
  'Last contact date',
  'Form of contact',
  'Who Contacted',
  'CIF/CNP',
  'Data emiterii',
  'Factura',
  'Moneda',
  'Valoare totala',
  'Rest de incasat',
  'Data scadentei',
  'Zile intarziere',
  'Status',
  'Case context',
  'Churn Risk',
  'Actions',
  'Final status',
  'Aditional agreements (addendum)',
  'Contract Amount',
  'Price/session',
  'Installments',
  'Amount for each installment',
  'Frequency',
  'Installments paid',
  'Installments remained to be paid',
  'How much paid so far',
  'How much remained to be paid',
  'Sessions in contract',
  'Sessions in agora',
  'Obs about sessions',
  'Sessions done',
  'Value of the sessions done',
  'Sessions paid but not done',
  'Sessions paid and not done yet value',
  'Payment promise amount',
  'Payment promise date',
  'Possible retention solution',
  'Strategy meeting date',
  'Strategy meeting link or agora',
];

export const contractsToCsv = (rows: ContractRowDTO[]) => {
  const lines: (string | number)[][] = [];

  for (const row of rows) {
    const invoices = row.invoices.length ? row.invoices : [null];
    const money = (value: number) => (row.hasContract ? value : '');
    const addendaText = row.addenda
      .map(
        (addendum) =>
          `${formatDate(addendum.date)}: +${addendum.sessions} sessions / ${addendum.amount}`
      )
      .join('; ');
    const installmentAmounts = row.installments
      .map((installment) => installment.amount)
      .join(' / ');
    const observations = [row.sessionsNotes, ...row.sessions.autoNotes].filter(Boolean).join(' | ');

    for (const invoice of invoices) {
      lines.push([
        row.client?.name ?? row.parent.fullName ?? '',
        row.contractNumber ?? '',
        row.contractUrl ?? '',
        row.addenda
          .map((addendum) => addendum.url)
          .filter(Boolean)
          .join('; '),
        row.student.fullName,
        row.parent.fullName ?? '',
        row.parent.email ?? '',
        row.parent.phone ?? '',
        row.strategist?.fullName ?? '',
        row.lastContact ? formatDate(row.lastContact.date) : '',
        row.lastContact ? CONTRACT_CONTACT_FORM_LABELS[row.lastContact.form] : '',
        row.lastContact?.by.fullName ?? '',
        row.client?.vatCode ?? '',
        invoice ? formatDate(invoice.issueDate) : '',
        invoice ? `${invoice.series} ${invoice.number}` : '',
        invoice ? invoice.currency : row.currency,
        invoice ? invoice.totalAmount : '',
        invoice ? invoice.unpaidAmount : '',
        invoice ? formatDate(invoice.dueDate) : '',
        invoice ? invoice.overdueDays : '',
        invoice ? CLIENT_INVOICE_STATUS_LABELS[invoice.status] : '',
        row.caseContext ?? '',
        row.churn ? `${row.churn.level} (${row.churn.score})` : '',
        row.actions ?? '',
        CONTRACT_STATUS_LABELS[row.status],
        addendaText,
        money(row.summary.totalAmount),
        money(row.summary.pricePerSession),
        row.hasContract ? row.installments.length : '',
        installmentAmounts,
        row.hasContract ? frequencyLabel(row) : '',
        money(row.summary.installmentsPaid),
        money(row.summary.installmentsRemaining),
        money(row.summary.paidAmount),
        money(row.summary.remainingAmount),
        money(row.summary.sessionsInContract),
        row.sessions.inAgora,
        observations,
        row.sessions.done,
        money(row.sessions.valueDone),
        money(row.sessions.paidNotDone),
        money(row.sessions.valuePaidNotDone),
        row.paymentPromise?.amount ?? '',
        row.paymentPromise ? formatDate(row.paymentPromise.date) : '',
        row.retentionSolution ?? '',
        row.strategyMeeting ? formatDate(row.strategyMeeting.scheduledDate) : '',
        row.strategyMeeting
          ? `${window.location.origin}/admin/meeting-management?meetingId=${row.strategyMeeting._id}`
          : '',
      ]);
    }
  }

  return stringify([CSV_HEADERS, ...lines]);
};

export const downloadCsv = (content: string, fileName: string) => {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(url);
};
